import gql from 'graphql-tag';
import React, { useEffect, useState } from 'react';
import { useMutation, useApolloClient } from 'react-apollo';
import styled from 'styled-components';

const CREATE_GDPR_CONSENT = gql`
  mutation CreateGdprConsent($input: GdprConsentInput!) {
    createGdprConsent(input: $input)
  }
`;

const LINK_ANON_CONSENT = gql`
  mutation LinkAnonConsentToUser($consentId: String!) {
    linkAnonConsentToUser(consentId: $consentId)
  }
`;

const BackgroundBlur = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9998;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  background: rgba(0, 0, 0, 0.2);
`;

const PopupContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 9999;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 20px;
  max-width: 420px;
  font-family: sans-serif;
`;

const Title = styled.h3`
  margin-top: 0;
  font-size: 1.1rem;
`;

const Text = styled.p`
  font-size: 0.9rem;
  margin: 8px 0;
`;

const ButtonGroup = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 8px 16px;
  background: #007aff;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #005ecc;
  }
  
  &:disabled {
    background: #aab8c2;
    cursor: not-allowed;
  }
`;

const DeclineButton = styled(Button)`
  background: #e53935;

  &:hover {
    background: #c62828;
  }
`;

function getOrCreateConsentId() {
  const cookieMatch = document.cookie.match(/(?:^|;\s*)consent_id=([^;]*)/);
  let id = cookieMatch ? cookieMatch[1] : null;
  if (!id) {
    id = crypto.randomUUID();
    document.cookie = `consent_id=${id}; path=/; samesite=strict; secure`;
  }
  return id;
}

export async function linkAnonConsentToUserWithApollo(client) {
  const consentId = getOrCreateConsentId();
  if (!consentId) return;
  try {
    await client.mutate({
      mutation: LINK_ANON_CONSENT,
      variables: { consentId }
    });
  } catch (err) {
    if (!String(err).includes('Not authenticated')) {
      console.warn('Failed to link GDPR consent:', err);
    }
  }
}

export function useLinkGdprConsentOnLogin(token) {
  const client = useApolloClient();
  useEffect(() => {
    if (token) {
      linkAnonConsentToUserWithApollo(client);
    }
    // eslint-disable-next-line
  }, [token]);
}

export default function GDPRModal({ onAccept }) {
  const [declined, setDeclined] = useState(false);
  const [createGdprConsent, { loading, error }] = useMutation(CREATE_GDPR_CONSENT);

  useEffect(() => {
    localStorage.setItem('gdpr_shown', 'true');
  }, []);

  const handleConsentAction = async (didAccept) => {
    const input = {
      consentGiven: didAccept,
      consentScope: didAccept ? 'full' : 'none',
      consentTextVer: '1.2',
      consentMethod: 'use_mutation_hook',
      consentId: getOrCreateConsentId(),
    };

    try {
      await createGdprConsent({ variables: { input } });
      if (didAccept) {
        onAccept();
      } else {
        setDeclined(true);
      }
    } catch (err) {
      console.error('Failed to send GDPR consent:', err);
    }
  };

  const handleGoHome = () => {
    window.location.href = '/signin';
  };

  if (error) {
    console.error("GraphQL Mutation Error:", error.message);
  }

  return (
    <>
      <BackgroundBlur />
      <PopupContainer>
        {!declined ? (
          <>
            <Title>Data Processing Notice (GDPR)</Title>
            <Text>
              We process and store your personal and health-related data on secure servers located in the
              European Economic Area (EEA), in compliance with GDPR.
            </Text>
            <Text>
              You retain all rights under the law, including access, correction, and deletion.
              By continuing, you agree to this processing and our{' '}
              <a href="https://www.menhealth.me/privacy-policy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>.
            </Text>
            <ButtonGroup>
              <Button onClick={() => handleConsentAction(true)} disabled={loading}>
                {loading ? 'Processing...' : 'Accept & Continue'}
              </Button>
              <DeclineButton onClick={() => handleConsentAction(false)} disabled={loading}>
                Decline
              </DeclineButton>
            </ButtonGroup>
          </>
        ) : (
          <>
            <Title>Access Denied</Title>
            <Text>You must accept the terms to use this app.</Text>
            <Button onClick={handleGoHome}>Go Back</Button>
          </>
        )}
      </PopupContainer>
    </>
  );
}
